import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Radio, BellRing, BellOff } from "lucide-react";
import { pushSupported, getPushStatus, enablePush, disablePush, registerSW } from "@/lib/push";
import { toast } from "sonner";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const NotificationBell = () => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushState, setPushState] = useState("off");
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    if (pushSupported()) { registerSW(); getPushStatus().then(setPushState); }
    return () => clearInterval(t);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState === "on") { await disablePush(); setPushState("off"); toast("Live alerts turned off"); }
      else {
        await enablePush(); setPushState("on");
        toast.success("Live alerts on! You'll get notified the moment creators go live.");
      }
    } catch (e) {
      if (e.message === "blocked") toast.error("Notifications are blocked in your browser settings.");
      else toast.error("Couldn't update notifications.");
      getPushStatus().then(setPushState);
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      try { await api.post("/notifications/read"); load(); } catch (_) {}
    }
  };

  const go = (link) => { setOpen(false); if (link) nav(link); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell"
        className="relative grid h-9 w-9 place-items-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:border-[#00FFC2] hover:text-white">
        <Bell size={17} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge"
            className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[#FF0055] px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-white/10 bg-[#0C0C14] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]">
          <div className="border-b border-white/10 px-4 py-3 font-display text-sm font-bold tracking-tight">
            Notifications
          </div>
          {pushSupported() && pushState !== "unsupported" && (
            <button onClick={togglePush} disabled={pushBusy} data-testid="push-toggle"
              className="flex w-full items-center justify-between gap-2 border-b border-white/10 px-4 py-2.5 text-left transition-colors hover:bg-white/5 disabled:opacity-60">
              <span className="flex items-center gap-2 text-sm text-zinc-300">
                {pushState === "on" ? <BellRing size={15} className="text-[#00FFC2]" /> : <BellOff size={15} className="text-zinc-500" />}
                Live alerts (browser push)
              </span>
              <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${pushState === "on" ? "bg-[#00FFC2]/15 text-[#00FFC2]" : "bg-white/5 text-zinc-400"}`}>
                {pushBusy ? "…" : pushState === "on" ? "On" : "Off"}
              </span>
            </button>
          )}
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-zinc-500" data-testid="notif-empty">
                No notifications yet. Follow creators to hear when they go live.
              </p>
            ) : (
              items.map((n) => (
                <button key={n.id} onClick={() => go(n.link)} data-testid={`notif-item-${n.id}`}
                  className={`flex w-full items-start gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/5 ${n.read ? "" : "bg-[#00FFC2]/[0.04]"}`}>
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#FF0055]/15 text-[#FF0055]">
                    <Radio size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{n.title}</p>
                    {n.body && <p className="truncate text-xs text-zinc-400">{n.body}</p>}
                    <p className="mt-0.5 font-mono text-[10px] text-zinc-600">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
